import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
} from "react";

import { useUpdateSKUMutation } from "@/data/mutations";
import { GalleryTabData, GalleryTabOptions } from "@/data/types";
import { removeGalleryItem, updateGalleryTabOptions } from "@/lib/cms";

import { useCMSContext } from "../cms-context";
import { useCMSTabContext } from "../cms-tab-context";

interface GalleryTabContextType {
  options: GalleryTabOptions;
  data: GalleryTabData[];
  onUpdateOptions: (options: GalleryTabOptions) => void;
  onDeleteItem: (itemIndex: number) => void;
}

const GalleryTabContext = createContext<GalleryTabContextType | null>(null);

type Props = PropsWithChildren<{
  options: GalleryTabOptions;
  data: GalleryTabData[];
}>;

export const GalleryTabProvider: FC<Props> = ({ children, options, data }) => {
  const { sku, pageContent } = useCMSContext();
  const updateSKUMutation = useUpdateSKUMutation(sku.id);
  const { tabIndex } = useCMSTabContext();

  const onUpdateOptions = useCallback(
    (options: GalleryTabOptions) => {
      updateSKUMutation.mutate({
        page_content: updateGalleryTabOptions(pageContent, options, tabIndex),
      });
    },
    [pageContent, tabIndex, updateSKUMutation],
  );

  const onDeleteItem = useCallback(
    (itemIndex: number) => {
      updateSKUMutation.mutate({
        page_content: removeGalleryItem(pageContent, tabIndex, itemIndex),
      });
    },
    [pageContent, tabIndex, updateSKUMutation],
  );

  return (
    <GalleryTabContext.Provider
      value={{ options, data, onUpdateOptions, onDeleteItem }}
    >
      {children}
    </GalleryTabContext.Provider>
  );
};

export const useGalleryTabContext = () => {
  const context = useContext(GalleryTabContext);

  if (!context) {
    throw new Error(
      "useGalleryTabContext must be used within an GalleryTabProvider",
    );
  }

  return context;
};
